<template>
  <UiDialog :open="open" :xl="!hasOpenPreview" :x2l="hasOpenPreview" @close="$emit('close')">
    <template v-if="issue" #header>
      <div class="flex min-w-0 flex-1 items-center gap-3">
        <img v-if="headerCompany && headerCompany.avatar" :src="headerCompany.avatar" :title="headerCompany.name"
             class="h-8 w-8 shrink-0 rounded-lg object-cover ring-1 ring-border" />
        <input v-model="form.title" placeholder="Titre de la conversation" @blur="saveField('title')"
               class="min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-2 py-1 text-lg font-semibold text-foreground outline-none transition-colors placeholder:font-normal placeholder:text-muted-foreground/60 hover:border-input focus:border-ring focus:bg-background focus:ring-2 focus:ring-ring/30" />
        <div class="w-40 shrink-0">
          <UiSelect v-model="form.status" class="h-8" @update:modelValue="saveField('status')">
            <option v-for="status in statuses" :key="status.id" :value="status.id">{{ status.label }}</option>
          </UiSelect>
        </div>
        <button type="button" :title="issue.archived_at ? 'Désarchiver' : 'Archiver'"
                class="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground" @click="archive">
          <ArchiveIcon class="h-4 w-4" />
        </button>
      </div>
    </template>

    <div v-if="issue" class="grid gap-5"
         :class="hasOpenPreview ? 'md:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]' : 'md:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]'">
      <!-- LEFT: details (ou aperçu fichier plein cadre) -->
      <div class="flex min-w-0 flex-col"
           :class="hasOpenPreview ? '' : 'space-y-5 overflow-y-auto pr-1'"
           :style="{ height: hasOpenPreview ? '74vh' : '69vh' }">
        <template v-if="hasOpenPreview">
          <div class="mb-2 flex items-center gap-2">
            <button type="button" class="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-accent hover:text-foreground" @click="activePreviewId = null">
              <XIcon class="h-4 w-4" /> Fermer l'aperçu
            </button>
            <span class="min-w-0 flex-1 truncate text-sm font-medium">{{ activeAttachment ? activeAttachment.filename : "" }}</span>
            <a v-if="activeAttachment" :href="attachmentFileUrl(activeAttachment)" target="_blank" class="shrink-0 text-xs text-muted-foreground hover:text-foreground">Ouvrir</a>
          </div>
          <ExcelPreview v-if="activeAttachment" :key="activeAttachment.id" :url="attachmentFileUrl(activeAttachment)" class="min-h-0 flex-1" />
        </template>
        <template v-else>
        <MarkdownEditor v-model="form.description" rows="5" placeholder="Description…" @blur="saveField('description')" />

        <!-- Rattachements -->
        <div class="space-y-2">
          <div class="flex flex-wrap items-center gap-1.5">
            <span class="mr-1 text-xs font-medium uppercase text-muted-foreground">Rattacher</span>
            <UiButton size="sm" variant="outline" @click="openPicker('transaction')"><SwitchHorizontalIcon class="h-3.5 w-3.5" /> Transaction</UiButton>
            <UiButton size="sm" variant="outline" @click="openPicker('invoice')"><DocumentTextIcon class="h-3.5 w-3.5" /> Facture</UiButton>
            <UiButton size="sm" variant="outline" @click="openPicker('company')"><BuildingOfficeIcon class="h-3.5 w-3.5" /> Fournisseur</UiButton>
          </div>

          <div v-if="issue.transactions.length" class="space-y-1">
            <div v-for="tx in issue.transactions" :key="tx.id" class="flex items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-sm">
              <SwitchHorizontalIcon class="h-4 w-4 shrink-0 text-muted-foreground" />
              <router-link :to="`/transaction/${tx.id}`" class="min-w-0 flex-1 truncate hover:underline">{{ tx.from_name || tx.description || tx.id }}</router-link>
              <span class="font-mono text-xs" :class="tx.amount < 0 ? 'text-destructive' : 'text-emerald-600'">{{ tx.amount }} €</span>
              <button class="text-muted-foreground hover:text-destructive" @click="unlink('transaction', tx.id)"><XIcon class="h-4 w-4" /></button>
            </div>
          </div>

          <div v-if="issue.invoices.length" class="space-y-1">
            <div v-for="inv in issue.invoices" :key="inv.id" class="flex items-center gap-2 rounded-md border bg-card px-2 py-1.5 text-sm">
              <DocumentTextIcon class="h-4 w-4 shrink-0 text-muted-foreground" />
              <router-link :to="`/invoice/${inv.id}`" class="min-w-0 flex-1 truncate hover:underline">{{ inv.company ? inv.company.name : inv.description || inv.id }}</router-link>
              <span class="font-mono text-xs">{{ inv.amount }} €</span>
              <button class="text-muted-foreground hover:text-destructive" @click="unlink('invoice', inv.id)"><XIcon class="h-4 w-4" /></button>
            </div>
          </div>

          <div v-if="issue.companies.length" class="space-y-1">
            <div v-for="co in issue.companies" :key="co.id" class="rounded-md border bg-card">
              <div class="flex items-center gap-2 px-2 py-1.5 text-sm">
                <img v-if="co.avatar" :src="co.avatar" class="h-4 w-4 shrink-0 rounded-full object-cover" />
                <BuildingOfficeIcon v-else class="h-4 w-4 shrink-0 text-muted-foreground" />
                <router-link :to="`/company/${co.id}`" class="min-w-0 flex-1 truncate hover:underline">{{ co.name }}</router-link>
                <button class="text-xs text-muted-foreground hover:text-foreground" @click="toggleRelated(co.id)">
                  {{ relatedOpen[co.id] ? "Masquer" : "Détails" }}
                </button>
                <button class="text-muted-foreground hover:text-destructive" @click="unlink('company', co.id)"><XIcon class="h-4 w-4" /></button>
              </div>
              <div v-if="relatedOpen[co.id] && related[co.id]" class="border-t px-2 py-1.5 text-xs">
                <p class="font-medium text-muted-foreground">Factures ({{ related[co.id].invoices.length }})</p>
                <router-link v-for="inv in related[co.id].invoices" :key="`i${inv.id}`" :to="`/invoice/${inv.id}`" class="flex items-center justify-between py-0.5 hover:underline">
                  <span class="min-w-0 flex-1 truncate">{{ inv.description || inv.invoice_id || inv.id }}</span>
                  <span class="font-mono">{{ inv.amount }} €</span>
                </router-link>
                <p class="mt-1 font-medium text-muted-foreground">Transactions ({{ related[co.id].transactions.length }})</p>
                <router-link v-for="tx in related[co.id].transactions" :key="`t${tx.id}`" :to="`/transaction/${tx.id}`" class="flex items-center justify-between py-0.5 hover:underline">
                  <span class="min-w-0 flex-1 truncate">{{ tx.from_name || tx.description || tx.id }}</span>
                  <span class="font-mono">{{ tx.amount }} €</span>
                </router-link>
              </div>
            </div>
          </div>

          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <h4 class="text-sm font-semibold">Checklists</h4>
              <UiButton size="sm" variant="outline" @click="addChecklist">Ajouter</UiButton>
            </div>
            <div v-for="checklist in issue.checklists" :key="checklist.id" class="rounded-md border p-2">
              <div class="mb-1 flex items-center justify-between">
                <span class="text-sm font-medium">{{ checklist.title }}</span>
                <div class="flex items-center gap-2">
                  <span class="text-xs text-muted-foreground">{{ checklistProgress(checklist) }}</span>
                  <button class="text-muted-foreground hover:text-destructive" @click="deleteChecklist(checklist.id)"><XIcon class="h-4 w-4" /></button>
                </div>
              </div>
              <div v-for="item in checklist.items" :key="item.id" class="flex items-center gap-2 py-0.5 text-sm">
                <input type="checkbox" :checked="item.is_done" class="h-4 w-4 rounded border-input" @change="toggleChecklistItem(checklist.id, item)" />
                <span :class="item.is_done ? 'text-muted-foreground line-through' : ''" class="min-w-0 flex-1 truncate">{{ item.content }}</span>
                <button class="text-muted-foreground hover:text-destructive" @click="deleteChecklistItem(checklist.id, item.id)"><XIcon class="h-3.5 w-3.5" /></button>
              </div>
              <div class="mt-1 flex gap-1">
                <UiInput v-model="checklistItemDrafts[checklist.id]" placeholder="Nouvel élément" class="h-8" />
                <UiButton size="sm" variant="ghost" :disabled="!checklistItemDrafts[checklist.id]" @click="addChecklistItem(checklist.id)">+</UiButton>
              </div>
            </div>
          </div>

          <div class="space-y-2">
            <h4 class="text-sm font-semibold">Pièces jointes</h4>
            <div v-for="att in issue.attachments" :key="att.id" class="flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm">
              <PaperClipIcon class="h-4 w-4 shrink-0 text-muted-foreground" />
              <a :href="att.file_id ? `https://billing.clafoutis.eu/api/file/${att.file_id}` : att.url" target="_blank" class="min-w-0 flex-1 truncate hover:underline">{{ att.filename }}</a>
              <button v-if="isExcel(att)" type="button" class="shrink-0 text-xs text-muted-foreground hover:text-foreground" @click="activePreviewId = att.id">Aperçu</button>
              <button class="text-muted-foreground hover:text-destructive" @click="removeAttachment(att.id)"><XIcon class="h-4 w-4" /></button>
            </div>
            <label class="inline-flex cursor-pointer items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <PaperClipIcon class="h-4 w-4" /> Ajouter un fichier
              <input type="file" class="hidden" @change="uploadAttachment" />
            </label>
          </div>

          <IssueHistory :issue="issue" :users="users" />
        </div>
        </template>
      </div>

      <!-- RIGHT: conversation -->
      <div class="flex min-w-0 flex-col overflow-hidden rounded-xl border border-border/70 bg-gradient-to-b from-muted/40 to-muted/10" :style="{ height: hasOpenPreview ? '74vh' : '69vh' }">
          <h4 class="flex items-center gap-1.5 border-b border-border/70 bg-background/60 px-3 py-2.5 text-sm font-semibold backdrop-blur">
            <ChatAltIcon class="h-4 w-4 text-primary" /> Discussion
            <span class="text-xs font-normal text-muted-foreground">({{ issue.comments.length }})</span>
          </h4>
          <div class="border-b border-border/70 bg-background/40 p-3">
            <MarkdownEditor v-model="newComment" :rows="composerExpanded ? 3 : 1"
                            placeholder="Écrire un commentaire…"
                            @focus="composerFocused = true" @blur="composerFocused = false" />
            <div v-if="composerExpanded" class="mt-2 flex justify-end">
              <UiButton variant="brand" :disabled="!newComment.trim()" @click="submitComment">Envoyer</UiButton>
            </div>
          </div>
          <div class="flex-1 space-y-3 overflow-y-auto p-3">
            <p v-if="!issue.comments.length" class="text-sm text-muted-foreground">Aucun message.</p>
            <div v-for="comment in sortedComments" :key="comment.id" class="flex gap-2.5">
              <UiAvatar :name="comment.user ? comment.user.name || comment.user.email : '?'" />
              <div class="min-w-0 flex-1 rounded-xl rounded-tl-sm border border-border/60 bg-card px-3 py-2 shadow-sm">
                <div class="flex items-center justify-between gap-2">
                  <span class="truncate text-xs font-semibold" :style="{ color: nameColor(comment) }">{{ comment.user ? comment.user.name || comment.user.email : "Inconnu" }}</span>
                  <span class="flex shrink-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span v-if="comment.updated_at && comment.updated_at !== comment.created_at" title="Message modifié">(modifié)</span>
                    <span>{{ formatDate(comment.created_at) }}</span>
                    <button v-if="isOwnComment(comment) && editingCommentId !== comment.id" type="button" title="Modifier"
                            class="rounded p-0.5 hover:bg-accent hover:text-foreground" @click="startEditComment(comment)">
                      <PencilIcon class="h-3.5 w-3.5" />
                    </button>
                  </span>
                </div>
                <template v-if="editingCommentId === comment.id">
                  <MarkdownEditor v-model="editingBody" rows="3" class="mt-1" />
                  <div class="mt-2 flex justify-end gap-2">
                    <UiButton size="sm" variant="ghost" @click="cancelEditComment">Annuler</UiButton>
                    <UiButton size="sm" variant="brand" :disabled="!editingBody.trim()" @click="saveEditComment">Enregistrer</UiButton>
                  </div>
                </template>
                <MarkdownView v-else :content="comment.body" class="mt-1 text-sm" />
                <div v-if="comment.reactions && comment.reactions.length" class="mt-2 flex flex-wrap gap-1">
                  <span v-for="r in comment.reactions" :key="r.emoji" :title="(r.names || []).join(', ')"
                        class="inline-flex items-center gap-1 rounded-full border border-border bg-background px-1.5 py-0.5 text-xs shadow-sm">
                    <span>{{ r.emoji }}</span>
                    <span class="text-muted-foreground">{{ (r.names || []).length }}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>

    <UiDialog :open="picker.open" :title="pickerTitle" wide @close="picker.open = false">
      <div class="space-y-2">
        <UiInput v-model="picker.query" placeholder="Rechercher…" @input="searchPicker" />
        <div class="max-h-64 space-y-1 overflow-y-auto">
          <button
            v-for="result in picker.results"
            :key="result.id"
            class="flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-sm hover:bg-accent"
            @click="pickResult(result)"
          >
            <span class="min-w-0 flex-1 truncate">{{ pickerLabel(result) }}</span>
            <span v-if="picker.type !== 'company'" class="font-mono text-xs">{{ result.amount }} €</span>
            <span v-if="result.date" class="text-xs text-muted-foreground">{{ result.date }}</span>
          </button>
        </div>
      </div>
    </UiDialog>
  </UiDialog>
</template>

<script>
import {
  ArchiveIcon,
  ChatAltIcon,
  DocumentTextIcon,
  OfficeBuildingIcon as BuildingOfficeIcon,
  PaperClipIcon,
  PencilIcon,
  SwitchHorizontalIcon,
  XIcon,
} from "@heroicons/vue/outline";
import { getUser } from "@/auth";
import UiAvatar from "@/components/ui/UiAvatar.vue";
import UiButton from "@/components/ui/UiButton.vue";
import UiDialog from "@/components/ui/UiDialog.vue";
import UiInput from "@/components/ui/UiInput.vue";
import UiSelect from "@/components/ui/UiSelect.vue";
import MarkdownEditor from "@/components/ui/MarkdownEditor.vue";
import MarkdownView from "@/components/ui/MarkdownView.vue";
import ExcelPreview from "@/components/ui/ExcelPreview.vue";
import IssueHistory from "@/components/board/IssueHistory.vue";
import { ISSUE_STATUSES, useBoardService } from "@/composables/boardService";

export default {
  name: "IssueDetail",
  components: {
    UiAvatar, UiButton, UiDialog, UiInput, UiSelect, MarkdownEditor, MarkdownView, ExcelPreview, IssueHistory,
    ArchiveIcon, ChatAltIcon, DocumentTextIcon, BuildingOfficeIcon, PaperClipIcon, PencilIcon, SwitchHorizontalIcon, XIcon,
  },
  props: {
    open: { type: Boolean, default: false },
    issue: { type: Object, default: null },
    users: { type: Array, default: () => [] },
    labels: { type: Array, default: () => [] },
  },
  emits: ["close", "updated", "labels-changed"],
  setup() {
    return { board: useBoardService(), authUser: getUser() };
  },
  data() {
    return {
      form: { title: "", description: "", status: "backlog", owner_id: "" },
      newComment: "",
      editingCommentId: null,
      editingBody: "",
      composerFocused: false,
      activePreviewId: null,
      checklistItemDrafts: {},
      relatedOpen: {},
      related: {},
      picker: { open: false, type: "transaction", query: "", results: [] },
      statuses: ISSUE_STATUSES,
    };
  },
  computed: {
    sortedComments() {
      return [...(this.issue?.comments || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    },
    composerExpanded() {
      return this.composerFocused || !!this.newComment.trim();
    },
    activeAttachment() {
      if (this.activePreviewId == null) return null;
      return (this.issue?.attachments || []).find((att) => att.id === this.activePreviewId) || null;
    },
    hasOpenPreview() {
      return !!this.activeAttachment && this.isExcel(this.activeAttachment);
    },
    headerCompany() {
      if (this.issue?.companies && this.issue.companies.length) return this.issue.companies[0];
      const inv = (this.issue?.invoices || []).find((i) => i.company);
      return inv ? inv.company : null;
    },
    transactionsTotal() {
      const sum = (this.issue?.transactions || []).reduce((acc, tx) => acc + Number(tx.amount || 0), 0);
      return sum ? sum.toFixed(2) : "";
    },
    invoicesTotal() {
      const sum = (this.issue?.invoices || []).reduce((acc, inv) => acc + Number(inv.amount || 0), 0);
      return sum ? sum.toFixed(2) : "";
    },
    pickerTitle() {
      return {
        transaction: "Lier une transaction",
        invoice: "Lier une facture",
        company: "Lier un fournisseur",
      }[this.picker.type];
    },
  },
  watch: {
    issue: {
      immediate: true,
      handler(issue, previous) {
        if (!issue) return;
        this.form = {
          title: issue.title || "",
          description: issue.description || "",
          status: issue.status,
          owner_id: issue.owner_id ? String(issue.owner_id) : "",
        };
        if (!previous || previous.id !== issue.id) {
          this.activePreviewId = null;
        }
      },
    },
  },
  methods: {
    formatDate(value) {
      return value ? new Date(value).toLocaleString("fr-FR") : "";
    },
    nameColor(comment) {
      const name = comment.user ? comment.user.name || comment.user.email : "?";
      let h = 0;
      for (let i = 0; i < name.length; i += 1) {
        h = (h * 31 + name.charCodeAt(i)) % 360;
      }
      return `hsl(${(h + 180) % 360} 45% 38%)`;
    },
    async saveField(field) {
      const value = this.form[field];
      if (this.issue[field] === value) return;
      const updated = await this.board.updateIssue(this.issue.id, { [field]: value === "" ? null : value });
      this.$emit("updated", updated);
    },
    async saveOwner() {
      const updated = await this.board.updateIssue(this.issue.id, {
        owner_id: this.form.owner_id ? Number(this.form.owner_id) : null,
      });
      this.$emit("updated", updated);
    },
    async archive() {
      const updated = await this.board.updateIssue(this.issue.id, { archived: !this.issue.archived_at });
      this.$emit("updated", updated);
      this.$emit("close");
    },
    async submitComment() {
      await this.board.addComment(this.issue.id, this.newComment.trim());
      this.newComment = "";
      this.composerFocused = false;
      this.$emit("updated", await this.board.getIssue(this.issue.id));
    },
    isOwnComment(comment) {
      return Boolean(comment.user && this.authUser && comment.user.email === this.authUser.email);
    },
    startEditComment(comment) {
      this.editingCommentId = comment.id;
      this.editingBody = comment.body;
    },
    cancelEditComment() {
      this.editingCommentId = null;
      this.editingBody = "";
    },
    async saveEditComment() {
      await this.board.updateComment(this.issue.id, this.editingCommentId, this.editingBody.trim());
      this.cancelEditComment();
      this.$emit("updated", await this.board.getIssue(this.issue.id));
    },
    async uploadAttachment(event) {
      const file = event.target.files[0];
      if (!file) return;
      await this.board.addAttachment(this.issue.id, file);
      event.target.value = "";
      this.$emit("updated", await this.board.getIssue(this.issue.id));
    },
    isExcel(att) {
      return /\.xlsx?$/i.test(att.filename || "");
    },
    attachmentFileUrl(att) {
      return att.file_id ? `https://billing.clafoutis.eu/api/file/${att.file_id}` : att.url;
    },
    async removeAttachment(attachmentId) {
      await this.board.deleteAttachment(this.issue.id, attachmentId);
      this.$emit("updated", await this.board.getIssue(this.issue.id));
    },
    openPicker(type) {
      this.picker = { open: true, type, query: "", results: [] };
      this.searchPicker();
    },
    pickerLabel(result) {
      if (this.picker.type === "transaction") return result.from_name || result.description || result.id;
      if (this.picker.type === "company") return result.name;
      return result.company ? result.company.name : result.description || result.id;
    },
    async searchPicker() {
      const query = this.picker.query;
      if (this.picker.type === "transaction") this.picker.results = await this.board.searchTransactions(query);
      else if (this.picker.type === "company") this.picker.results = await this.board.searchCompanies(query);
      else this.picker.results = await this.board.searchInvoices(query);
    },
    async pickResult(result) {
      let updated;
      if (this.picker.type === "transaction") updated = await this.board.linkTransaction(this.issue.id, result.id);
      else if (this.picker.type === "company") updated = await this.board.linkCompany(this.issue.id, result.id);
      else updated = await this.board.linkInvoice(this.issue.id, result.id);
      this.picker.open = false;
      this.$emit("updated", updated);
    },
    async unlink(type, id) {
      if (type === "transaction") await this.board.unlinkTransaction(this.issue.id, id);
      else if (type === "company") await this.board.unlinkCompany(this.issue.id, id);
      else await this.board.unlinkInvoice(this.issue.id, id);
      this.$emit("updated", await this.board.getIssue(this.issue.id));
    },
    async toggleRelated(companyId) {
      this.relatedOpen = { ...this.relatedOpen, [companyId]: !this.relatedOpen[companyId] };
      if (this.relatedOpen[companyId] && !this.related[companyId]) {
        this.related = { ...this.related, [companyId]: await this.board.companyRelated(companyId) };
      }
    },
    checklistProgress(checklist) {
      const done = checklist.items.filter((item) => item.is_done).length;
      return `${done}/${checklist.items.length}`;
    },
    async addChecklist() {
      const title = window.prompt("Titre de la checklist");
      if (!title) return;
      await this.board.addChecklist(this.issue.id, title);
      this.$emit("updated", await this.board.getIssue(this.issue.id));
    },
    async deleteChecklist(checklistId) {
      await this.board.deleteChecklist(this.issue.id, checklistId);
      this.$emit("updated", await this.board.getIssue(this.issue.id));
    },
    async addChecklistItem(checklistId) {
      const content = this.checklistItemDrafts[checklistId];
      if (!content) return;
      await this.board.addChecklistItem(this.issue.id, checklistId, content);
      this.checklistItemDrafts[checklistId] = "";
      this.$emit("updated", await this.board.getIssue(this.issue.id));
    },
    async toggleChecklistItem(checklistId, item) {
      await this.board.updateChecklistItem(this.issue.id, checklistId, item.id, { is_done: !item.is_done });
      this.$emit("updated", await this.board.getIssue(this.issue.id));
    },
    async deleteChecklistItem(checklistId, itemId) {
      await this.board.deleteChecklistItem(this.issue.id, checklistId, itemId);
      this.$emit("updated", await this.board.getIssue(this.issue.id));
    },
  },
};
</script>
